import React from 'react';
import { AlertTriangle, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

/**
 * QuitDrillDialog — Iter 81 (Feb 2026).
 *
 * Two-choice quit confirm shown when a reader hits "Quit" mid-drill.
 *   • Quit only          → drop the session, don't save.
 *   • Save & quit        → bookmark {passage_id, wpm, chunk_idx, chunk_size}
 *                          so `/learn` can render a "Resume where you
 *                          left off" card next time.
 * Cancel closes the dialog and keeps the drill running.
 */
const QuitDrillDialog = ({
  open,
  onOpenChange,
  onSaveAndQuit,
  onQuitOnly,
  progressPct = 0,
  saving = false,
  drillLabel = 'drill',
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-red-500/30 text-white max-w-md"
        data-testid="quit-drill-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            Quit this {drillLabel}?
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            You&apos;re {progressPct}% of the way through. Save your spot so you
            can pick up where you left off, or quit and start fresh next
            time.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-gray-300 hover:text-white sm:mr-auto"
            data-testid="quit-drill-cancel"
          >
            Keep going
          </Button>
          <Button
            variant="outline"
            onClick={onQuitOnly}
            className="border-slate-700 text-white hover:bg-slate-800"
            data-testid="quit-drill-only"
          >
            <X className="w-4 h-4 mr-1" /> Quit
          </Button>
          <Button
            onClick={onSaveAndQuit}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-black"
            data-testid="quit-drill-save"
          >
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : 'Save & quit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuitDrillDialog;
